import { useEffect, useState } from "react";
import { RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { TRY, NUM } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { IMG } from "@/components/pos/PosBits";
import { r2 } from "@/lib/pos";
import { unitGrossOf } from "@/lib/receipt";

// Satış iadesi (kısmi / tam): iade adetleri stoğa geri eklenir
export function ReturnDialog({ sale, onClose, onDone }) {
  const [lines, setLines] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { setLines({}); }, [sale]);

  const submit = async () => {
    const payload = Object.entries(lines).map(([product_id, quantity]) => ({ product_id, quantity: Number(quantity) || 0 })).filter((l) => l.quantity > 0);
    if (!payload.length) return toast.error("İade edilecek adet girin");
    setSaving(true);
    try {
      const { data } = await api.post(`/quick-sales/${sale.id}/return`, { lines: payload, restore_stock: true });
      toast.success(`İade alındı · ${TRY(data.returned_gross)} stoğa iade edildi${data.fully_returned ? " · tam iade" : ""}`);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const items = sale?.items || [];
  const totalRet = items.reduce((a, it) => a + (Number(lines[it.product_id]) || 0) * unitGrossOf(it), 0);
  return (
    <Dialog open={!!sale} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-[96vw] sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="qs-return-dialog">
        <DialogHeader><DialogTitle className="font-head text-xl flex items-center gap-2"><RotateCcw className="h-5 w-5 text-amber-600" />Satış İadesi</DialogTitle></DialogHeader>
        {sale && (
          <>
            <p className="text-sm text-muted-foreground">İade edilecek adetleri girin. Seçilen ürünler <b>stoğa geri eklenir</b>. Kısmi iade desteklenir.</p>
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {items.map((it) => {
                const sold = Number(it.quantity) || 0;
                const already = Number(it.returned) || 0;
                const remain = r2(sold - already);
                const val = lines[it.product_id] ?? "";
                return (
                  <div key={it.product_id} className="flex items-center gap-2 rounded-xl border p-2.5" data-testid={`qs-ret-item-${it.product_id}`}>
                    <IMG src={it.image_url} alt={it.name} className="h-9 w-9" />
                    <div className="min-w-0 flex-1"><div className="text-sm font-medium truncate">{it.name}</div><div className="text-[11px] text-muted-foreground tnum">Satılan {NUM(sold)}{already > 0 ? ` · iade ${NUM(already)}` : ""} · kalan {NUM(remain)} {it.unit}</div></div>
                    <div className="flex items-center gap-1">
                      <Input type="number" min="0" max={remain} value={val} onChange={(e) => setLines((r) => ({ ...r, [it.product_id]: e.target.value }))} className="h-8 w-20 text-center tnum" placeholder="0" disabled={remain <= 0} data-testid={`qs-ret-qty-${it.product_id}`} />
                      <Button size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={() => setLines((r) => ({ ...r, [it.product_id]: remain }))} disabled={remain <= 0} data-testid={`qs-ret-all-${it.product_id}`}>Tümü</Button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-2.5 flex items-center justify-between">
              <span className="text-sm font-medium text-amber-800">İade Tutarı</span>
              <span className="font-head font-black tnum text-lg text-amber-700" data-testid="qs-ret-total">{TRY(r2(totalRet))}</span>
            </div>
            <div className="flex justify-end gap-2 pt-1">
              <Button variant="outline" onClick={onClose} data-testid="qs-ret-cancel">Vazgeç</Button>
              <Button onClick={submit} disabled={saving} className="bg-amber-600 hover:bg-amber-700" data-testid="qs-ret-confirm"><RotateCcw className="h-4 w-4 mr-1.5" />{saving ? "İşleniyor..." : "İadeyi Onayla"}</Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
